import { Ref, computed, ref, watch } from "@vue/composition-api";
import {
  PlaybookDataDescriptor,
  PlaybookDataExecutionStatus,
  PlaybookDescriptor,
} from "./model";
import * as api from "./api";
import "./api.mock";
import { SourceApplicationsDescriptors } from "@/views/customize/data-sources/data/data-relations/applicaitons.map";
import { SharedState } from "@/common/state";
import { UseDropdownItems } from "@/common/composition/dropdown-items";
import {
  ApplicationType,
  DataSourceType,
  ScanSession,
  SourceApplication,
} from "@/views/customize/data-sources/data/model";
import { state as data_sources_state } from "@/views/customize/data-sources/data/state";
import { POSTURE_TO_APPS_MAPPING } from "@/views/customize/data-sources/data/data-relations/postures-applications.map";
import { UseLoading } from "@/common/composition/loading";
import { FindingType } from "@/common/api/findings/findings.model";
import { merge_by } from "@/fp";
const scripts: Ref<PlaybookDescriptor[]> = ref([]);

const load_scripts = async () => {
  const res = await api.get_playbooks();
  if (res.success) {
    scripts.value = res.value;
  }
};

const use_playbook = (playbook_id: string) => {
  const playbook = scripts.value.find((d) => d.id == playbook_id)!;

  const recently_updated_instance_id = ref("");
  /**
   * using this var and function to enable row animation
   * based on the  recently updated instance row id
   * */
  const update_changed_row_id = (id: string) => {
    recently_updated_instance_id.value = id;
    setTimeout(() => {
      recently_updated_instance_id.value = "";
    }, 2000);
  };

  const [
    instances,
    is_loading_instances,
    load_instances,
  ] = api.get_playbook_data_instances([]);

  const load_instances_from_vars = async (session_id: string, vars: any) => {
    load_instances({
      playbook_id: playbook_id,
      filters: {
        findingSessionId: session_id,
        applicationId: vars["{~application}"],
        findingTypes: vars["{~finding_type}"]
          ? [vars["{~finding_type}"]]
          : undefined,
        labelIds: vars["{~label}"] ? [vars["{~label}"]] : undefined,
      },
    });
  };

  const instances_keys_map: Ref<{ [key: string]: number }> = ref({});
  const same_criteria_instances_status: PlaybookDataExecutionStatus[] = [
    "BuildScheduled",
    "Building",
    "Finished",
    "Finishing",
    "New",
    "Ready",
    "RunScheduled",
    "Running",
  ];
  const populate_instances_keys_map = () => {
    instances_keys_map.value = instances.value.reduce((acc, ins, index) => {
      if (same_criteria_instances_status.includes(ins.status)) {
        const key = gen_key_from_criteria({
          app_id: ins.scope.applicationId,
          finding_type: ins.scope.findingTypes[0],
          label_id: ins.scope.labelIds[0],
          service_id: ins.scope.serviceId,
          session_id: ins.scope.sessionId,
        });
        if (key in acc) {
          // skip
        } else {
          acc[key] = index;
        }
      }
      return acc;
    }, {} as { [key: string]: number });
  };

  const gen_key_from_criteria = (payload: {
    finding_type: FindingType;
    session_id: number;
    app_id: ApplicationType;
    service_id: DataSourceType;
    label_id: string;
  }) => {
    return [
      payload.finding_type,
      payload.session_id,
      payload.service_id,
      payload.app_id,
      payload.label_id,
    ].join("__");
  };
  const do_we_have_an_instance_with_the_same_props = (payload: {
    finding_type: FindingType;
    session_id: number;
    app_id: ApplicationType;
    service_id: DataSourceType;
    label_id: string;
  }): { has: boolean; index: number } => {
    const key = gen_key_from_criteria(payload);
    const has = key in instances_keys_map.value;
    let index = -1;
    if (key in instances_keys_map.value) {
      index = instances_keys_map.value[key];
    }

    return {
      has,
      index,
    };
  };

  const apps_for_posture = POSTURE_TO_APPS_MAPPING[playbook.posture];
  const available_apps_for_source: SourceApplication[] = data_sources_state.ext.get_available_apps_for_source(
    playbook.criteria.serviceId
  );

  const available_apps_for_playbook = available_apps_for_source.filter((f) =>
    apps_for_posture.includes(f.id)
  );

  const [
    source_applications,
    selected_source_application,
    set_selected_source_application,
  ] = UseDropdownItems<SourceApplication>(available_apps_for_playbook);

  const is_sessions_ready = ref(false);
  let sessions_cb: Function;
  const unsub = watch(is_sessions_ready, (is_ready) => {
    if (is_ready) {
      sessions_cb && sessions_cb();
      unsub();
    }
  });
  const on_sessions_ready = (cb: () => void) => {
    sessions_cb = cb;
    if (is_sessions_ready.value) {
      sessions_cb();
    }
  };

  const [
    available_sessions,
    selected_session,
    set_selected_session,
    init_available_sessions,
  ] = UseDropdownItems<ScanSession & { name: string }>([]);
  const [is_loading_sessions, set_is_loading_sessions] = UseLoading(
    true
  ).as_arr();
  const load_available_sessions = async () => {
    set_is_loading_sessions(true);
    const res = await data_sources_state.ext.get_sessions_for_source(
      playbook.criteria.serviceId
    );
    if (res.success) {
      const sessions = res.value.filter((s) => s.status !== "Opened");
      init_available_sessions(sessions);
      if (sessions.length > 0) {
        // @ts-ignore
        set_selected_session(sessions[0]);
      }
      is_sessions_ready.value = true;
    }
    set_is_loading_sessions(false);
  };

  const data_instances = computed(() =>
    instances.value.map((ins) => {
      return {
        ...ins,
        display: {
          animate: recently_updated_instance_id.value === ins.id,
          application: SourceApplicationsDescriptors[ins.scope.applicationId],
          findings: ins.scope.findingTypes.map((f) => f.replace(/_/g, " ")),
          information: ins.scope.labelIds.map((id) =>
            SharedState.getInfoType(id)
          ),
        },
      };
    })
  );
  const [is_generating_data, set_is_generating_data] = UseLoading(
    false
  ).as_arr();

  // create instance and add it to the list at the top
  const generate_data = async (
    app_id: ApplicationType,
    finding_type: FindingType,
    label: string
  ) => {
    set_is_generating_data(true);
    const res = await api.create_playbook_data_instance({
      playbook_id,
      scope: {
        itemsCount: 0,
        applicationId: app_id,
        findingTypes: [finding_type],
        labelIds: [label],
        serviceId: playbook.criteria.serviceId,
        sessionId: +selected_session.value!.id,
      },
    });
    if (res.success) {
      instances.value = [res.value, ...instances.value];
      update_changed_row_id(res.value.id);
    }
    set_is_generating_data(false);
    populate_instances_keys_map();
  };

  // change the status of the int
  const stop_running_or_building = async (instance: PlaybookDataDescriptor) => {
    let new_status: PlaybookDataExecutionStatus;
    if (instance.status === "Running") {
      const cancel_res = await api.stop_playbook_instance_running({
        instance_id: instance.id,
        playbook_id: instance.playbookId,
      });
      if (cancel_res.success) {
        new_status = cancel_res.value;
      }
    } else {
      const build_cancel_res = await api.cancel_playbook_instance_building({
        instance_id: instance.id,
        playbook_id: instance.playbookId,
      });
      if (build_cancel_res.success) {
        new_status = build_cancel_res.value;
      }
    }
    instances.value = instances.value.map((d) => {
      if (d.id == instance.id) {
        return {
          ...d,
          status: new_status,
        };
      }
      return d;
    });
    update_changed_row_id(instance.id);
  };

  const download_data = async (instance: PlaybookDataDescriptor) => {
    const playbook = scripts.value.find((d) => d.id == instance.playbookId)!;
    await api.download_data_instance({
      instance,
      playbook_name: playbook.action,
    });
  };
  const upload_data = () => {
    /*  */
  };

  const run_cloud_script_against_data_instance = async (
    instance: PlaybookDataDescriptor
  ) => {
    const res = await api.run_playbook_instance({
      playbook_id: instance.playbookId,
      instance_id: instance.id,
    });
    if (res.success) {
      instances.value = instances.value.map((d) => {
        if (d.id === instance.id) {
          return {
            ...d,
            status: "RunScheduled",
          };
        }
        return d;
      });

      update_changed_row_id(instance.id);
    }
  };

  let pooling_task_id = 0;

  const in_progress_status: PlaybookDataExecutionStatus[] = [
    "Building",
    "Finishing",
    "RunScheduled",
    "BuildScheduled",
    "Running",
    "New",
    "Terminating",
    "StopScheduled",
    "Stopping",
  ];

  const merge_status = merge_by<PlaybookDataDescriptor, "id">("id");

  const init = async () => {
    populate_instances_keys_map();

    await load_available_sessions();
    pooling_task_id = setInterval(async () => {
      const in_progress_instances_ids = instances.value
        .filter((ins) => in_progress_status.includes(ins.status))
        .map((ins) => ins.id);

      if (in_progress_instances_ids.length > 0) {
        const playbook_id = instances.value[0].playbookId;
        const res = await api.get_playbook_instance_status({
          instance_ids: in_progress_instances_ids,
          playbook_id,
        });
        if (res.success) {
          // @ts-ignore
          instances.value = merge_status(instances.value)(res.value);

          const ready_or_running_instances = instances.value.filter(
            (d) =>
              d.status === "Ready" ||
              d.status === "Running" ||
              d.status == "Finishing"
          );

          if (ready_or_running_instances.length > 0) {
            const playbook_id = ready_or_running_instances[0].playbookId;
            // update the items count
            const res2 = await api.get_specific_playbook_data_instances({
              playbook_id,
              instances: ready_or_running_instances,
            });
            if (res2.success) {
              // @ts-ignore
              instances.value = merge_by("id")(instances.value)(res2.value);
            }
          }
        }
        populate_instances_keys_map();
      }
    }, 5000);
  };

  const destroy = () => {
    pooling_task_id && clearInterval(pooling_task_id);
  };

  return {
    destroy,
    init,
    load_instances,
    load_instances_from_vars,
    instances_keys_map,
    do_we_have_an_instance_with_the_same_props,
    load_available_sessions,
    playbook,
    recently_updated_instance_id,
    is_loading_instances,
    is_generating_data,
    generate_data,
    data_instances,
    stop_running_or_building,
    download_data,
    upload_data,
    run_cloud_script_against_data_instance,
    // application
    source_applications,
    selected_source_application,
    set_selected_source_application,
    // sessions
    is_loading_sessions,
    available_sessions,
    selected_session,
    on_sessions_ready,
    set_selected_session,
  };
};

const init = async () => {
  await load_scripts();
};

export const cloud_playbooks_state = {
  init,
  scripts,
  use_playbook,
};
